import React, { useState } from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import FileUploadOne from "../FileUpload/FileUploadOne";

interface ReuploadComponentProps {
  itemId: string;
  onReupload: (itemId: string, file: File) => Promise<void>;
  allowedFileTypes?: string[];
  onSuccess?: () => void;
}

const ReuploadComponent: React.FC<ReuploadComponentProps> = ({
  itemId,
  onReupload,
  allowedFileTypes = ["jpeg", "jpg", "pptx", "docx", "doc", "pdf", "ppt", "xlsx", "xls", "xlsm"],
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showReuploadField, setShowReuploadField] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension && allowedFileTypes.includes(fileExtension)) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError(`File type not allowed. Allowed types: ${allowedFileTypes.join(", ")}.`);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleReuploadClick = () => {
    setShowReuploadField(!showReuploadField);
    setSelectedFile(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      await onReupload(itemId, selectedFile);
      setSelectedFile(null);
      setShowReuploadField(false);
      onSuccess?.();
    } catch (err) {
      setError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {!showReuploadField ? (
        <Button
          variant="outlined"
          onClick={handleReuploadClick}
          sx={{
            color: 'black',
            borderColor: 'black',
            '&:hover': { borderColor: 'black' },
          }}
        >
          Reupload
        </Button>
      ) : (
        <>
          <FileUploadOne
            file1={selectedFile}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
          />
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            sx={{
                color: 'black',
                margin: '0px 10px',
                borderColor: 'black',
                '&:hover': {
                  borderColor: 'black',
                },
              }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
          {uploading? (
            null
          ):(
              <Button
              variant="outlined"
              onClick={handleReuploadClick}
              sx={{ ml: 1, color:'#000', borderColor:'#000' }}
              
            >
              Cancel
            </Button>
          )}
        
        </>
      )}
      
      {error && (
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default ReuploadComponent;